import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import Loader from '@lwt-helix/loader';
import Tab from '@lwt-helix/tab';
import Modal from '@lwt-helix/modal';
import Overview from './tabs/Overview';
import ContactInformation from './tabs/ContactInformation';
import GrowthAnalysis from './tabs/GrowthAnalysis';
import ListingProficiency from './tabs/ListingProficiency';
import {
    getDefaultOverviewColumns,
    getDefaultContactInfoColumns,
    getSearchDateYearDetails,
    getMlsRestrictionSettings,
    tabIndexedViews,
    tabIndexes,
} from '../helpers/agentProductionHelpers';
import getAgentChartSettings from './chart/AgentChartTableSettings.js';
import { agentProductionTerms, emails, TimePeriods } from '../../constants';
import { agentsColumns, tableViewNames, tabModes, viewHideAgentsDropdownLabels } from '../constants/agentProductionConstants';
import {
    getDefaultGrowthAnalysisColumnsLtm,
    getDefaultGrowthAnalysisColumnsYtd
} from './helpers/growthAnalysisHelpers';
import TitleRow from '../components/common/TitleRow';
import { setStateData } from '../../common/helpers/state';
import { getKeyByValue } from '../../common/helpers/object';
import { getDefaultColumns as getDefaultListingProfColumns } from '../helpers/listingProficiencyHelpers';
import { setTimeIntervalData } from '../helpers/browserStorageHelper';
import { useCommonStore, useSearchStore } from '../../store/store';
import { NoResultsMessage } from '../../common/components/search/NoResultsMessage';
import { exportCsv } from './common/exportCsv.jsx';
import {
    useContactInfoAdditionalHeaders,
    useGrowthAnalysisAdditionalHeaders,
    useListingProfAdditionalHeaders,
    useOverviewAdditionalHeaders
} from '../hooks/agGrid.js';
import { getSortedObjectsNumeric } from '../../market-share/helpers/helpers.jsx';
import AddAgentListPopover from './common/AddAgentListPopover';
import { HTTP_RESPONSES, responseKeys } from '../../constants/service.js';
import { getRankedData } from '../../common/helpers/agGrid.js';
import { getDateRangeText } from '../../common/helpers/titles.js';
import { getIntervalLastDates  } from "../components/helpers/savedAgents";

const AgentProduction = ({ agentData, module }) => {
    const {
        selectedMls,
        selectedSavedSearch,
        loadedSearchCriteria,
        isLoadingSearchData,
        setSummaryPinnedData,
        searchStatus,
        currentTab,
        setCurrentTab,
        viewHideAgentsDropdownValue,
        crossSortOrder,
        sortByStoredOrder,
        period,
        timePeriod,
        clearPMSelectedColumnIds
    } = useSearchStore(state => ({
        selectedMls: state[module]?.selectedMls,
        selectedSavedSearch: state[module]?.selectedSavedSearch,
        loadedSearchCriteria: state[module]?.loadedSearchCriteria,
        isLoadingSearchData: state.isLoadingSearchData,
        setSummaryPinnedData: state.setSummaryPinnedData,
        searchStatus: state[module]?.searchStatus,
        currentTab: state[module].currentTab,
        setCurrentTab: state.setCurrentTab,
        viewHideAgentsDropdownValue: state[module]?.selectedViewHideAgentsFilter,
        crossSortOrder: state[module]?.crossSortOrder,
        sortByStoredOrder: state.sortByStoredOrder,
        period: state[state.selectedModule]?.loadedSearchCriteria?.timePeriod?.intervalType?.toString(),
        timePeriod: state[state.selectedModule]?.loadedSearchCriteria?.timePeriod,
        clearPMSelectedColumnIds: state.clearPMSelectedColumnIds
    }));

    const {
        officeInfo,
        timeIntervals,
        clearAgentListPopover
    } = useCommonStore(state => ({
        officeInfo: state.officeInfo,
        timeIntervals: state.timeIntervals,
        clearAgentListPopover: state.clearAgentListPopover
    }));

    const restrictedMlsSettings = getMlsRestrictionSettings(selectedMls?.mlsId);

    const applyStoredColumns = (defaultCols, tabMode) => {
    const state = useSearchStore.getState();
    const storedIds = state[state.selectedModule]?.selectedColumnIds?.[tabMode]?.columns;
    if (!storedIds) return defaultCols;
    return defaultCols.map(col => ({
        ...col,
        isDisplayed: storedIds.includes(col.id) || storedIds.includes(col.parentColumnId)
    }));
};

    const [state, setState] = useState({
        showModal: false,
        modalProps: {
            dataLwtId: 'agent-production-modal',
            show: false,
            size: '',
            title: '',
            footerClassName: '',
            showFooter: false,
            onClose: () => null,
            children: null
        },
        currentAgentChart: null,
        growthAnalysisPeriod: agentProductionTerms.last12Months,
        listingProficiencyColumns: applyStoredColumns(getDefaultListingProfColumns(), tabModes.LISTING_PROFICIENCY),
        overviewColumns: applyStoredColumns(getDefaultOverviewColumns(), tabModes.OVERVIEW),
        contactInfoColumns: applyStoredColumns(getDefaultContactInfoColumns(restrictedMlsSettings), tabModes.CONTACT_INFORMATION),
        growthAnalysisColumnsLtm: applyStoredColumns(getDefaultGrowthAnalysisColumnsLtm(), tableViewNames.growthAnalysisLtm),
        growthAnalysisColumnsYtd: applyStoredColumns(getDefaultGrowthAnalysisColumnsYtd(), tableViewNames.growthAnalysisYtd),
        sortedLtmGrowthAnalysisColumn: agentsColumns.totalVolume.id,
        sortedYtdGrowthAnalysisColumn: agentsColumns.totalVolume.id,
        sortedListingProfColumn: agentsColumns.totalVolume.id,
        selectedTimePeriod: null,
        currentSearchDates: {
            lastIntervalText: '',
            from: null,
            to: null
        },
        timeIntervals: [],
        accessibleOfficeList: [],
        selectedData: agentData,
        userPrivileges: undefined,
        agentInfo: undefined
    });

    const sortedSelectedData = useMemo(() => {
        return sortByStoredOrder(state.selectedData)?.map(d => ({...d, hasFavStar: true}));
    }, [state.selectedData, crossSortOrder])

    useEffect(() => {
        const tabIndexedView = tabIndexedViews[currentTab];
        loadedSearchCriteria && !searchStatus && setSummaryPinnedData(tabIndexedView, responseKeys.recruitingSoldTotals);
    }, [state.selectedData, currentTab]);

    const accessibleOfficeSet = state?.accessibleOfficeList?.length ? new Set(state?.accessibleOfficeList?.map(obj => obj.mlsOfficeId?.officeId)) : new Set();

    const toggleModal = () => {
        setStateData('showModal', !state.showModal, setState);
    };

    useEffect(() => clearAgentListPopover(), []);

    useEffect(() => {
        officeInfo && setStateData('accessibleOfficeList', officeInfo, setState);
    }, [officeInfo]);

    useEffect(() => {
        if (timeIntervals) {
            setStateData('timeIntervals', timeIntervals, setState);
            setTimeIntervalData(timeIntervals);
        }
    }, [timeIntervals]);

    useEffect(() => {
        setStateData(
            'title',
            selectedSavedSearch?.savedSearch?.searchName || agentProductionTerms.proficiencyMetrics,
            setState
        );
    }, [selectedSavedSearch]);

    useEffect(() => {
        if (
            loadedSearchCriteria?.timePeriod &&
            !state.selectedTimePeriod
        ) {
            setStateData('selectedTimePeriod', loadedSearchCriteria.timePeriod, setState);
        }
    }, [loadedSearchCriteria, state.selectedTimePeriod]);

    useEffect(() => {
        if (agentData === undefined) {
            setStateData('selectedData', undefined, setState);
        } else if (agentData.constructor.name === 'Object' && agentData.error) {
            setStateData('showModal', true, setState);
        }
        resetTableColsAndSortsToDefault();
    }, [agentData]);

    const prevMlsIdRef = useRef(selectedMls?.mlsId);

    useEffect(() => {
        const prevMlsId = prevMlsIdRef.current;
        prevMlsIdRef.current = selectedMls?.mlsId;
        if (!selectedMls?.mlsId || prevMlsId === selectedMls?.mlsId || !prevMlsId) return;
        clearPMSelectedColumnIds();
        setState(prevState => ({
            ...prevState,
            overviewColumns: getDefaultOverviewColumns(),
            contactInfoColumns: getDefaultContactInfoColumns(restrictedMlsSettings),
            growthAnalysisColumnsLtm: getDefaultGrowthAnalysisColumnsLtm(),
            growthAnalysisColumnsYtd: getDefaultGrowthAnalysisColumnsYtd(),
            listingProficiencyColumns: getDefaultListingProfColumns()
        }));
    }, [selectedMls?.mlsId, restrictedMlsSettings]);

    useEffect(() => {
        if (agentData) {
            const sortedData = getSortedObjectsNumeric(agentData, agentsColumns.totalVolume.id);
            const revisedData = getRankedData(sortedData, agentsColumns.totalVolume.id);
            switch (viewHideAgentsDropdownValue) {
                case viewHideAgentsDropdownLabels.viewOnlyMyAgents:
                    setStateData('selectedData', revisedData?.filter(element => accessibleOfficeSet?.has(element.officeId)), setState);
                    break;
                case viewHideAgentsDropdownLabels.hideMyAgents:
                    setStateData('selectedData', revisedData?.filter(element => !accessibleOfficeSet?.has(element.officeId)), setState);
                    break;
                case viewHideAgentsDropdownLabels.viewHideAgents:
                    setStateData('selectedData', revisedData, setState);
                    break;
                default:
                    setStateData('selectedData', revisedData, setState);
            }
        }
    }, [viewHideAgentsDropdownValue, agentData, state.accessibleOfficeList]);

    useEffect(() => {

        if (!state.selectedTimePeriod || !state.timeIntervals.length) return;
        let currentSearchDates;
        if (state.selectedTimePeriod.intervalType !== TimePeriods['Custom Date']) {
            const interval = state.timeIntervals?.find(i => i?.intervalType === state.selectedTimePeriod.intervalType);
            if (interval) {
                const lastIntervalText = getKeyByValue(TimePeriods, state.selectedTimePeriod.intervalType);
                const from = new Date(`${interval.last.from}Z`);
                const to = new Date(`${interval.last.to}Z`);
                currentSearchDates = { lastIntervalText, from, to };
            }
        } else {
            const lastIntervalText = state.selectedTimePeriod.intervalType;
            const from = new Date(state.selectedTimePeriod.startDate);
            const to = new Date(state.selectedTimePeriod.endDate);
            currentSearchDates = { lastIntervalText, from, to };
        }
        setStateData('currentSearchDates', currentSearchDates, setState);
    }, [state.selectedTimePeriod, state.timeIntervals]);

    useEffect(() => {
        setStateData(
            'currentAgentChart',
            state.selectedData?.length > 0 ? state.selectedData[0] : null,
            setState
        );
        !state.selectedData && setStateData('showModal', false, setState);
    }, [state.selectedData]);

    const handleAgentChart = (agent) =>
        setStateData('currentAgentChart', agent, setState);

    const { chartOptions, chartSchema } = getAgentChartSettings(handleAgentChart);

    const searchPeriodDates = timeIntervals?.length && getIntervalLastDates(timeIntervals, period, timePeriod);
    const last12MonthsDates = timeIntervals?.length && getIntervalLastDates(timeIntervals, agentProductionTerms.annually);
    const dateRangesText = {
        currentSearch: getDateRangeText(searchPeriodDates),
        allMls: getDateRangeText(last12MonthsDates),
    };

    const setSortedLtmGrowthAnalysisColumn = (field) => {
        setStateData('sortedLtmGrowthAnalysisColumn', field, setState);
    };

    const setSortedYtdGrowthAnalysisColumn = (field) => {
        setStateData('sortedYtdGrowthAnalysisColumn', field, setState);
    };

    const setSortedListingProfColumn = (field) => {
        setStateData('sortedListingProfColumn', field, setState);
    };

    const resetTableColsAndSortsToDefault = () => {
        [ setSortedLtmGrowthAnalysisColumn,
            setSortedYtdGrowthAnalysisColumn, setSortedListingProfColumn].map(setOption => {
            setOption('totalVolume');
        });
    };

    const additionalTableHeaders = useOverviewAdditionalHeaders(
        state.overviewColumns,
        state.selectedData?.length,
        dateRangesText,
    );

    const growthTableHeadersLtm = useGrowthAnalysisAdditionalHeaders(
        state.growthAnalysisColumnsLtm
    );

    const growthTableHeadersYtd = useGrowthAnalysisAdditionalHeaders(
        state.growthAnalysisColumnsYtd
    );

    const contactInfoAdditionalHeaders = useContactInfoAdditionalHeaders(
        state.contactInfoColumns,
        dateRangesText,
    );

    const proficiencyTableHeaders = useListingProfAdditionalHeaders(
        state.listingProficiencyColumns
    );

    const isExportDisabled = state.selectedData?.length === 0;

    const setListingProficiencyColumns = columns => {
        setStateData('listingProficiencyColumns', columns, setState);
    };

    const setOverviewColumns = (overviewColumns) => {
        setStateData('overviewColumns', overviewColumns, setState);
    };

    const setContactInfoColumns = (contactInfoColumns) => {
        setStateData('contactInfoColumns', contactInfoColumns, setState);
    };

    const setGrowthAnalysisColumnsYtd = cols => {
        setStateData('growthAnalysisColumnsYtd', cols, setState);
    };

    const setGrowthAnalysisColumnsLtm = (growthAnalysisColumnsLtm) => {
        setStateData('growthAnalysisColumnsLtm', growthAnalysisColumnsLtm, setState);
    };

    const setGrowthAnalysisPeriod = (period) => {
        setStateData('growthAnalysisPeriod', period, setState);
    };

    const yearDetails = getSearchDateYearDetails(state.currentSearchDates, currentTab);

    const headerProps = {
        module: module,
        isExportDisabled,
        exportCsv: () => exportCsv(state, setState, currentTab, state.selectedData),
        showExportButton: true,
        title: state.title,
        tab: currentTab,
        growthAnalysisPeriod: state.growthAnalysisPeriod,
        agentData: state.selectedData,
        year: yearDetails,
        showShareButton: false
    };

    const initialSearchNotRun = <>
        <TitleRow
            title={state.title}
            module={module}
            showExportButton={false}
            showHideAgentsDropdown={false}
        />
        <div className='text-center'>
            <br />
            <p className='initial-search'>
                {agentProductionTerms.initialLoad}
            </p>
        </div>
    </>;

    const loader = <div className='py-3'><Loader /></div>;
    const resultsFound = <>
        <Modal {...state.modalProps} show={state.showModal} />
        <TitleRow {...headerProps} showHideAgentsDropdown={true} />
        <div className='content'>
            <Tab
                navSize={4}
                contentSize={4}
                items={[
                    {
                        title: agentProductionTerms.overview,
                        content: {
                            jsx: (
                                <Overview
                                    agentData={sortedSelectedData}
                                    chartOptions={chartOptions}
                                    chartSchema={chartSchema}
                                    currentAgentChart={state.currentAgentChart}
                                    additionalTableHeaders={additionalTableHeaders}
                                    columns={state.overviewColumns}
                                    setColumns={setOverviewColumns}
                                    module={module}
                                    state={state}
                                    setState={setState}
                                    isActive={currentTab === tabIndexes.OVERVIEW}
                                />
                            )
                        }
                    },
                    {
                        title: agentProductionTerms.contactInformation,
                        content: {
                            jsx: (
                                <ContactInformation
                                    additionalTableHeaders={contactInfoAdditionalHeaders}
                                    agentData={sortedSelectedData}
                                    columns={state.contactInfoColumns}
                                    setColumns={setContactInfoColumns}
                                    state={state}
                                    setState={setState}
                                    isActive={currentTab === tabIndexes.CONTACT_INFORMATION}
                                />
                            )
                        }
                    },
                    {
                        title: agentProductionTerms.growthAnalysis,
                        content: {
                            jsx: (
                                <GrowthAnalysis
                                    agentData={sortedSelectedData}
                                    additionalTableHeadersLtm={growthTableHeadersLtm}
                                    additionalTableHeadersYtd={growthTableHeadersYtd}
                                    growthAnalysisPeriod={state.growthAnalysisPeriod}
                                    setGrowthAnalysisPeriod={setGrowthAnalysisPeriod}
                                    ltmColumns={state.growthAnalysisColumnsLtm}
                                    ytdColumns={state.growthAnalysisColumnsYtd}
                                    setColumnsLtm={setGrowthAnalysisColumnsLtm}
                                    setColumnsYtd={setGrowthAnalysisColumnsYtd}
                                    currentSearchDates={state.currentSearchDates}
                                    state={state}
                                    setState={setState}
                                    isActive={currentTab === tabIndexes.GROWTH_ANALYSIS}
                                />
                            )
                        }
                    },
                    {
                        title: agentProductionTerms.listingProficiency,
                        content: {
                            jsx: (<ListingProficiency
                                    agentData={sortedSelectedData}
                                    additionalTableHeaders={proficiencyTableHeaders}
                                    setColumns={setListingProficiencyColumns}
                                    columns={state.listingProficiencyColumns}
                                    state={state}
                                    setState={setState}
                                    isActive={currentTab === tabIndexes.LISTING_PROFICIENCY}
                                />
                            )
                        }
                    }
                ]}
                tabs
                currentIndex={currentTab}
                setCurrentIndex={(index) => setCurrentTab(index)}
            />
            <AddAgentListPopover />
        </div>
    </>;


    const noResultsFound = <>
        <TitleRow
            title={state.title}
            module={module}
            showExportButton={false}
            showHideAgentsDropdown={false}
        />
        <div className='text-center'>
            <br />
            <NoResultsMessage/>
        </div>
    </>;

    const errorCodes = [
        HTTP_RESPONSES.badRequest,
        HTTP_RESPONSES.notFound,
        HTTP_RESPONSES.conflict,
    ];

    const noResponse = <>
        <TitleRow
            title={state.title}
            module={module}
            showExportButton={false}
            showHideAgentsDropdown={false}
        />
        <div className='content text-center'>
            <br />
            <p className='initial-search'>{agentProductionTerms.initialLoad}</p>
        </div>
        {
            !errorCodes.includes(state.selectedData?.response)
            && <Modal
                {...state.modalProps}
                title={<p className='font-weight-bold'>Error</p>}
                showFooter={true}
                show={state.showModal}
                onClose={toggleModal}
            >
                {<div className='pb-30'>
                    {
                        !state.selectedData?.response || state.selectedData?.response !== HTTP_RESPONSES.payloadTooLarge
                            ? <>
                                {agentProductionTerms.errorServerNoResponse}
                                <a
                                    href={`mailto:${emails.lwolfSupport}`}
                                    className='underlined-text'
                                >
                                    {emails.lwolfSupport}
                                </a>.
                            </>
                            : state.selectedData?.response === HTTP_RESPONSES.payloadTooLarge
                            && agentProductionTerms.errorServer413
                    }
                </div>}
            </Modal>
        }
    </>;

    const noOnlyMyAgentsResultsFound = <>
        <TitleRow
            {...headerProps}
            title={state.title}
            showHideAgentsDropdown={true}
        />
        <div className='content text-center'>
            <br />
            <p className='no-search-results-suggestion'>
                {agentProductionTerms.viewOnlyMyAgentsNoData}
            </p>
        </div>
    </>;

    const isInitialSearchNotRun = loadedSearchCriteria === undefined;

    const hasResults = !!state.selectedData?.length;

    const hasNoResults = state.selectedData?.length === 0;

    const hasNoResponse = state.selectedData?.constructor.name === 'Object';

    const hasNoOnlyMyAgentsResults = viewHideAgentsDropdownValue === viewHideAgentsDropdownLabels.viewOnlyMyAgents
        && state.selectedData?.constructor.name === 'Array' && state.selectedData?.length === 0;

    if (isLoadingSearchData) {
        return loader;
    }

    if (isInitialSearchNotRun) {
        return initialSearchNotRun;
    }

    if (hasNoResponse) {
        return noResponse;
    }

    if (hasResults) {
        return resultsFound;
    }

    if (hasNoOnlyMyAgentsResults) {
        return noOnlyMyAgentsResultsFound;
    }

    if (hasNoResults) {
        return noResultsFound;
    }
    return loader;
};

AgentProduction.propTypes = {
    agentData: PropTypes.array,
    module: PropTypes.string
};

export default AgentProduction;
